<script setup lang="ts">
	import { useCefFilterStore } from '@/stores/cefFilter'
	import { usePreviewCefStore } from '@/stores/previewCef'
	import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/24/solid'
	import { storeToRefs } from 'pinia'
	import { onMounted, provide, ref, watch, watchEffect } from 'vue'

	import BreadCrumb from '@/components/BreadCrumb.vue'
	import LoadingModal from '@/components/LoadingModal.vue'
	import StyledButton from '@/components/StyledButton.vue'
	import StyledInput from '@/components/StyledInput.vue'

	import useState from '@/composables/useState.ts'

	import { State } from '@/interfaces/state'

	import useCompaniesSearch from '@/modules/companies/composables/useCompaniesSearch'
	import useCompany from '@/modules/companies/composables/useCompany'
	import CefFilters from '@/modules/trips/components/CefFilters.vue'
	import CefListTable from '@/modules/trips/components/CefListTable.vue'
	import PreviewRequest from '@/modules/trips/components/PreviewRequest.vue'
	import useCefFilter from '@/modules/trips/composables/useCefFilter'
	import { useCefRequests } from '@/modules/trips/composables/useCefsRequests'
	import { Cef } from '@/modules/trips/interfaces/cef'

	import useCefs from '../composables/useCefs'

	const selectedCef = ref<Cef>()
	const paginationContainer = ref<HTMLElement>()
	const activeTab = ref<string>('current')

	// Flag para controlar si es la primera carga desde el widget
	const isInitialLoad = ref(true)

	const { previewCef, clearPreviewCef } = usePreviewCefStore()

	const tabs = [
		{
			id: 'current',
			name: 'En Curso',
			color: 'text-blue-600 border-blue-600 bg-blue-50',
		},
		{
			id: 'past',
			name: 'Anteriores',
			color: 'text-gray-600 border-gray-600 bg-gray-50',
		},
		{
			id: 'future',
			name: 'Próximos',
			color: 'text-green-600 border-green-600 bg-green-50',
		},
		{
			id: 'cancelled',
			name: 'Cancelados',
			color: 'text-red-600 border-red-600 bg-red-50',
		},
	]

	const { companies, setQuery, refetch } = useCompaniesSearch({
		immediate: true,
	})
	const { states } = useState()

	const { params } = useCefFilterStore()
	const { selectedCompany } = storeToRefs(useCefFilterStore())

	const {
		data: cefs,
		page,
		pagination,
		isLoading,
		setPage,
		searchParams,
		isFetching: isFetchingData,
		isRefetching,
	} = useCefs(params)
	// Set progressStage based on active tab

	const clearSpecificFilters = () => {
		params.cef = undefined
		params.confirmation = ''
		params.name = ''
		params.travelerName = ''
		params.creatorName = ''
		params.supplierName = ''
	}

	const changeTab = (newTab: string) => {
		if (activeTab.value !== newTab) {
			isInitialLoad.value = false
			activeTab.value = newTab
		}
	}

	watch(
		activeTab,
		(newTab) => {
			if (newTab === 'cancelled') {
				params.status = states.value.find(
					(state) => state.displayName === 'Cancelado'
				)?.id
				params.progressStage = null
			} else {
				params.status = null
				params.progressStage = newTab
			}

			if (!isInitialLoad.value) {
				clearSpecificFilters()
			}

			params.page = 1
			page.value = 1
		},
		{ immediate: true }
	)

	const handlePage = async (newPage: string | number) => {
		const pageNumber = Number(newPage)
		if (
			pageNumber >= 1 &&
			pageNumber <= pagination.value?.meta.lastPage! &&
			!isFetchingData.value
		) {
			if (paginationContainer.value) {
				paginationContainer.value.scrollIntoView({
					behavior: 'smooth',
					block: 'start',
				})
			}

			setPage(pageNumber)
			await searchParams()
		}
	}

	// Optimización: prevenir doble click
	const throttledHandlePage = (newPage: string | number) => {
		if (!isFetchingData.value) {
			handlePage(newPage)
		}
	}

	const { activeFilters } = useCefFilter(params)

	watch(params, () => {
		page.value = 1
		searchParams()
	})

	const { company } = useCompany(
		{
			companyId: 0,
		},
		{
			immediate: false,
		}
	)

	const {
		cefId,
		refetch: refetchRequets,
		isFetching,
	} = useCefRequests({
		params: {
			cefId: selectedCef.value?.id,
		},
		immediate: false,
	})

	const resetFilters = () => {
		params.company = undefined
		params.travelerName = ''
		params.order = 'desc'
		params.name = ''
		params.status =
			activeTab.value === 'cancelled' ?
				states.value?.find((state: State) => state.displayName === 'Cancelado')
					?.id
			:	null

		params.progressStage =
			activeTab.value === 'cancelled' ? null : activeTab.value
		params.cef = undefined
		params.confirmation = ''
		company.value = undefined
		params.creatorName = ''
		params.channel = undefined
		params.creationDates = []
		params.dates = []
		params.folio = undefined
		params.hasLetter = undefined
		params.service = undefined
		params.supplierName = ''

		// handleResetFilters()
	}

	const pages = [{ name: 'Viajes', to: '/trips', current: false }]

	const openSliderFilter = ref(false)

	const toggleSliderFilter = () => {
		openSliderFilter.value = false
	}

	provide('cef', {
		searchParams,
	})

	const handlePreviewRequest = (cef: Cef) => {
		selectedCef.value = cef
	}

	const closePreviewRequest = () => {
		selectedCef.value = undefined
	}

	watchEffect(() => {
		if (selectedCef.value) {
			cefId.value = selectedCef.value.id
			refetchRequets()
		}
	})

	onMounted(async () => {
		if (previewCef) {
			params.cef = String(previewCef.cefId)
			activeTab.value = previewCef.cefProgressStage
			await searchParams()
			isInitialLoad.value = false

			clearPreviewCef()
		} else {
			isInitialLoad.value = false
		}
	})

	watch(
		() => [cefs.value, previewCef],
		() => {
			if (previewCef && cefs.value?.data) {
				const foundCef = cefs.value.data.find(
					(cef) => cef.id === previewCef.cefId
				)

				if (foundCef) {
					selectedCef.value = foundCef
					cefId.value = foundCef.id
					refetchRequets()

					clearPreviewCef()
				}
			}
		},
		{
			once: previewCef !== undefined,
		}
	)
</script>

<template>
	<BreadCrumb :pages="pages"></BreadCrumb>

	<main class="flex justify-center items-center mt-4 px-4 sm:px-8 w-full">
		<div class="flex flex-col lg:flex-row w-full gap-4">
			<div
				class="flex flex-col gap-2 w-full"
				:class="[selectedCef ? 'lg:w-2/3' : '']"
			>
				<!-- Header Section with Tabs -->
				<div class="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
					<div
						class="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6"
					>
						<div>
							<h1 class="font-bold text-2xl lg:text-3xl text-gray-900">
								Gestión de Itinerarios
							</h1>
							<p class="text-gray-600 mt-1">
								{{
									selectedCompany ?
										`${selectedCompany?.name}`
									:	'Administra y supervisa todos los itinerarios'
								}}
							</p>
						</div>

						<div
							class="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center"
						>
							<StyledInput
								v-model="params.name"
								placeholder="Buscar por nombre de itinerario"
								class="w-full sm:w-80"
								input-class="border border-gray-300 w-full h-[42px] rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
								:debounce-time="500"
							/>

							<StyledButton
								class="rounded-lg border border-gray-300 px-6 py-3 bg-white hover:bg-gray-50 transition-colors duration-200 w-full sm:w-auto shadow-sm"
								@click="openSliderFilter = !openSliderFilter"
							>
								<div class="flex items-center justify-center gap-3">
									<svg
										class="w-5 h-5 text-gray-500"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z"
										></path>
									</svg>
									<span class="text-gray-700 font-medium">Filtros</span>
									<span
										v-if="activeFilters.length > 0"
										class="bg-blue-500 text-xs text-white rounded-full px-2 py-1 min-w-[20px] h-5 flex items-center justify-center font-medium"
									>
										{{ activeFilters.length }}
									</span>
								</div>
							</StyledButton>
						</div>
					</div>

					<!-- Tabs Navigation -->
					<div class="border-b border-gray-200">
						<nav class="-mb-px flex space-x-8">
							<button
								v-for="tab in tabs"
								:key="tab.id"
								@click="changeTab(tab.id)"
								:class="[
									activeTab === tab.id ?
										tab.color
									:	'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50',
									'whitespace-nowrap py-3 px-4 border-b-2 font-semibold text-sm rounded-t-lg transition-all duration-200',
								]"
							>
								<div class="flex items-center gap-2">
									<span>{{ tab.name }}</span>
								</div>
							</button>

							<!-- Indicador de filtro activo -->
							<div
								v-if="
									activeFilters.find((filter) => filter.reference === 'cef')
								"
								class="flex items-center gap-2 py-3 px-4 text-sm font-semibold text-amber-800 bg-amber-50 border border-amber-300 rounded-lg ml-4 shadow-sm"
							>
								<svg
									class="w-4 h-4 text-amber-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z"
									></path>
								</svg>
								<div class="flex flex-row gap-2">
									<span class="text-xs text-amber-700 opacity-75"
										>Filtrado por itinerario:</span
									>
									<span class="font-bold text-amber-900">
										{{
											activeFilters.find((filter) => filter.reference === 'cef')
												?.value
										}}
									</span>
								</div>
								<button
									@click="params.cef = undefined"
									class="ml-2 text-amber-600 hover:text-amber-800 hover:bg-amber-100 rounded-full p-1 transition-colors"
									title="Quitar filtro"
								>
									<svg
										class="w-3 h-3"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M6 18L18 6M6 6l12 12"
										></path>
									</svg>
								</button>
							</div>
						</nav>
					</div>
				</div>

				<template v-if="cefs">
					<!-- Información de resultados con diseño empresarial -->
					<div
						v-if="pagination"
						class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-100"
					>
						<div class="flex items-center gap-4">
							<div
								class="w-4 h-4 bg-blue-500 rounded-full flex-shrink-0 shadow-sm"
							></div>
							<div>
								<p
									v-if="cefs.data.length > 0"
									class="text-sm text-gray-800 font-semibold"
								>
									Mostrando
									<span class="font-bold text-blue-700">{{
										pagination.meta.from
									}}</span>
									-
									<span class="font-bold text-blue-700">{{
										pagination.meta.to
									}}</span>
									de
									<span class="font-bold text-blue-600 text-base">{{
										pagination.meta.total
									}}</span>
									itinerarios
								</p>
								<p
									v-else
									class="text-sm text-gray-600 font-medium"
								>
									No se encontraron itinerarios para este filtro
								</p>
							</div>
						</div>

						<!-- Status indicator profesional -->
						<div
							class="flex items-center gap-4 text-xs text-gray-600 font-medium"
						>
							<div
								class="flex items-center gap-2 bg-white rounded-lg px-3 py-2 shadow-sm"
							>
								<div
									:class="[
										'w-2 h-2 rounded-full',
										isFetchingData || isRefetching ?
											'bg-amber-400 animate-pulse'
										:	'bg-green-500',
									]"
								></div>
								<span>
									{{
										isFetchingData ? 'Cargando página...'
										: isRefetching ? 'Actualizando...'
										: isLoading ? 'Cargando...'
										: 'Actualizado'
									}}
								</span>
							</div>
							<div
								class="hidden sm:flex items-center gap-2 bg-white rounded-lg px-3 py-2 shadow-sm"
							>
								<span
									>Página {{ pagination.meta.currentPage }} de
									{{ pagination.meta.lastPage }}</span
								>
							</div>
						</div>
					</div>

					<!-- Contenedor de tabla con referencia -->
					<div
						ref="paginationContainer"
						class="relative bg-white rounded-xl border border-gray-200 overflow-hidden"
					>
						<!-- Overlay de carga usando estados nativos -->
						<transition
							enter-active-class="transition-all duration-300 ease-out"
							enter-from-class="opacity-0"
							enter-to-class="opacity-100"
							leave-active-class="transition-all duration-200 ease-in"
							leave-from-class="opacity-100"
							leave-to-class="opacity-0"
						>
							<div
								v-if="isFetchingData && !isLoading"
								class="absolute inset-0 bg-white/90 backdrop-blur-sm z-20 flex items-center justify-center"
							>
								<div
									class="flex flex-col items-center gap-4 p-6 bg-white rounded-xl shadow-lg border border-gray-100"
								>
									<div class="relative">
										<div
											class="w-10 h-10 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"
										></div>
										<div
											class="absolute inset-0 w-10 h-10 border-4 border-transparent border-t-blue-400 rounded-full animate-spin"
											style="animation-duration: 0.75s"
										></div>
									</div>
									<div class="text-center">
										<p class="text-sm font-semibold text-gray-900 mb-1">
											Cargando página...
										</p>
										<p class="text-xs text-gray-500">
											Por favor espera un momento
										</p>
									</div>
								</div>
							</div>
						</transition>

						<!-- Tabla de contenido -->
						<CefListTable
							:cefs="cefs"
							:selected-cef="selectedCef"
							:is-fetching="isFetchingData"
							:is-loading="isLoading"
							:is-refetching="isRefetching"
							@select-cef="handlePreviewRequest"
							@close-preview-request="closePreviewRequest"
							@refetch="searchParams"
						/>
					</div>

					<!-- Paginación moderna -->
					<div
						v-if="pagination && pagination.meta.lastPage > 1"
						class="flex flex-col sm:flex-row items-center justify-between gap-4 mt-3 mb-3 pt-3 border-t border-gray-100"
					>
						<div
							class="flex sm:hidden items-center gap-2 text-sm text-gray-600"
						>
							<span
								>Página {{ pagination.meta.currentPage }} de
								{{ pagination.meta.lastPage }}</span
							>
						</div>

						<div class="flex items-center justify-center sm:justify-start">
							<nav
								class="flex items-center gap-1"
								aria-label="Pagination"
							>
								<!-- Botón anterior -->
								<button
									:disabled="
										pagination.meta.currentPage === 1 || isFetchingData
									"
									:class="[
										'flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200',
										pagination.meta.currentPage === 1 || isFetchingData ?
											'text-gray-400 cursor-not-allowed bg-gray-50'
										:	'text-gray-700 hover:text-blue-600 hover:bg-blue-50 bg-white border border-gray-200 hover:border-blue-200',
									]"
									@click="throttledHandlePage(+page - 1)"
								>
									<ChevronLeftIcon class="w-4 h-4" />
									<span class="hidden sm:inline">Anterior</span>
								</button>

								<!-- Números de página -->
								<div class="flex items-center gap-1 mx-2">
									<template
										v-for="link in pagination.meta?.links?.filter((link) =>
											link.label.match(/\d/)
										)"
										:key="link.label"
									>
										<button
											:disabled="isFetchingData"
											:class="[
												'w-10 h-10 text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-center',
												link.active ?
													'bg-blue-600 text-white shadow-sm'
												:	'text-gray-700 hover:text-blue-600 hover:bg-blue-50 bg-white border border-gray-200 hover:border-blue-200',
												isFetchingData && 'opacity-50 cursor-not-allowed',
											]"
											@click="throttledHandlePage(link.label)"
										>
											<span v-if="!isFetchingData || !link.active">{{
												link.label
											}}</span>
											<div
												v-else
												class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
											></div>
										</button>
									</template>
								</div>

								<!-- Botón siguiente -->
								<button
									:disabled="
										pagination.meta.currentPage === pagination.meta.lastPage ||
										isFetchingData
									"
									:class="[
										'flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200',
										(
											pagination.meta.currentPage ===
												pagination.meta.lastPage || isFetchingData
										) ?
											'text-gray-400 cursor-not-allowed bg-gray-50'
										:	'text-gray-700 hover:text-blue-600 hover:bg-blue-50 bg-white border border-gray-200 hover:border-blue-200',
									]"
									@click="throttledHandlePage(+page + 1)"
								>
									<span class="hidden sm:inline">Siguiente</span>
									<ChevronRightIcon class="w-4 h-4" />
								</button>
							</nav>
						</div>

						<!-- Info adicional -->
						<div
							class="hidden sm:flex items-center gap-4 text-sm text-gray-500"
						>
							<div
								class="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5"
							>
								<svg
									class="w-4 h-4"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
									></path>
								</svg>
								<span class="font-medium"
									>{{ pagination.meta.total }} registros</span
								>
							</div>
							<div
								class="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5"
							>
								<svg
									class="w-4 h-4"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
									></path>
								</svg>
								<span class="font-medium"
									>{{ pagination.meta.perPage }} por página</span
								>
							</div>
						</div>
					</div>
				</template>
			</div>

			<div
				v-if="selectedCef"
				class="w-full fixed lg:sticky inset-0 lg:inset-auto lg:top-4 z-30 lg:z-10 bg-white/80 lg:bg-transparent backdrop-blur-sm lg:backdrop-blur-none overflow-y-auto lg:max-h-[80vh]"
				:class="[selectedCef ? 'lg:w-2/5' : 'lg:hidden']"
			>
				<PreviewRequest
					:cef="selectedCef"
					@close-preview-request="closePreviewRequest"
					:isFetching="isFetching"
				/>
			</div>
		</div>
	</main>

	<CefFilters
		:active-tab="activeTab"
		:states="states"
		:companies="companies"
		v-model:company="params.company"
		v-model:traveler-name="params.travelerName"
		v-model:order="params.order"
		v-model:state="params.active"
		v-model:cef="params.cef"
		v-model:name="params.name"
		v-model:status="params.status"
		v-model:progressStage="params.progressStage"
		v-model:confirmation="params.confirmation"
		v-model:creator-name="params.creatorName"
		v-model:channel="params.channel"
		v-model:creation-dates="params.creationDates"
		v-model:dates="params.dates"
		v-model:folio="params.folio"
		v-model:has-letter="params.hasLetter"
		v-model:service="params.service"
		v-model:supplier-name="params.supplierName"
		@reset-filters="resetFilters"
		@search="setQuery"
		:open-slide-over="openSliderFilter"
		@toggleFilter="toggleSliderFilter"
		@refetchCompanies="refetch"
	/>

	<!-- LoadingModal solo para carga inicial -->
	<LoadingModal :when="isLoading" />
</template>

<style scoped>
	/* Animaciones suaves */
	.transition-all {
		transition-property: all;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
	}

	/* Efectos hover mejorados */
	button:hover:not(:disabled) {
		transform: translateY(-1px);
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
	}

	button:active:not(:disabled) {
		transform: translateY(0);
	}

	/* Estados disabled */
	button:disabled {
		transform: none !important;
		box-shadow: none !important;
	}

	/* Spinner personalizado */
	@keyframes spin {
		to {
			transform: rotate(360deg);
		}
	}

	.animate-spin {
		animation: spin 1s linear infinite;
	}

	/* Backdrop blur para navegadores que lo soporten */
	@supports (backdrop-filter: blur(8px)) {
		.backdrop-blur-sm {
			backdrop-filter: blur(8px);
		}
	}
</style>
